import React from 'react';
import PropTypes from 'prop-types';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Box, Button, Flex, Image, Text
} from 'components/ui';
import HistorySectionCard from 'components/Content/BeautyInsider/BeautyInsiderHistory/HistorySectionCard';
import HistorySectionStatus from 'components/Content/BeautyInsider/BeautyInsiderHistory/HistorySectionStatus';
import localeUtils from 'utils/LanguageLocale';
import { colors, space } from 'style/config';
import { HISTORY_CARD_STYLES, HISTORY_SECTION_STATUS } from 'components/Content/BeautyInsider/BeautyInsiderHistory/historyConstants';
import EN from './locales/HistoryYearAtGlanceSection_en_US';
import FR from './locales/HistoryYearAtGlanceSection_fr_CA';

const getText = localeUtils.getLocalisedDataFromResource(EN, FR);

const iconSources = {
    employeeDiscountPurchase: '/img/ufe/icons/saving.svg',
    promosAndDiscountApplied: '/img/ufe/icons/saving.svg',
    beautyInsiderCashApplied: '/img/ufe/icons/points-cash.svg',
    creditCardRewardsEarned: '/img/ufe/icons/cc.svg',
    rougeRewardsEarned: '/img/ufe/icons/rouge-rewards.svg'
};

const SHOP_NOW_BUTTON_MIN_WIDTH = space[9] + space[5];
const PLACEHOLDER_SECTION_MIN_HEIGHT = space[9] - space[1];

/**
 * Renders the Year at a Glance History section from section presentation data.
 *
 * Supports loading, error, placeholder, empty, and populated statuses. Populated sections render ordered annual
 * savings/rewards items, while empty sections render the shop-now CTA when provided.
 *
 * @param {Object} props
 * @param {Object} props.section Year at a Glance presentation data.
 * @returns {React.ReactElement} Year at a Glance card.
 */
const HistoryYearAtGlanceSection = ({ section }) => {
    const isPlaceholder = section.status === HISTORY_SECTION_STATUS.PLACEHOLDER;

    const renderContent = () => {
        if (
            section.status === HISTORY_SECTION_STATUS.LOADING ||
            section.status === HISTORY_SECTION_STATUS.ERROR ||
            section.status === HISTORY_SECTION_STATUS.PLACEHOLDER
        ) {
            return (
                <HistorySectionStatus
                    loadingLabel={getText('loading')}
                    retryLabel={getText('retry')}
                    section={section}
                />
            );
        }

        if (section.status === HISTORY_SECTION_STATUS.EMPTY) {
            return (
                <Box>
                    {section.emptyState?.illustration && (
                        <Image
                            display='block'
                            size={48}
                            marginBottom={3}
                            src={section.emptyState.illustration}
                        />
                    )}
                    <Text is='p'>{section.emptyState?.body}</Text>
                    {section.emptyState?.primaryAction?.action && (
                        <Button
                            variant='secondary'
                            size='sm'
                            minWidth={SHOP_NOW_BUTTON_MIN_WIDTH}
                            marginTop={3}
                            onClick={section.emptyState.primaryAction.action}
                        >
                            {section.emptyState.primaryAction.label || getText('shopNow')}
                        </Button>
                    )}
                </Box>
            );
        }

        return (
            <Box>
                {section.items.map(item => (
                    <Flex
                        key={item.type}
                        alignItems='center'
                        css={styles.row}
                    >
                        <Image
                            src={iconSources[item.type]}
                            width={32}
                            height={32}
                            marginRight={3}
                        />
                        <Box flex={1}>
                            <Text
                                is='p'
                                children={item.label}
                            />
                            {item.subtitle && (
                                <Text
                                    is='p'
                                    color='gray'
                                    fontSize='sm'
                                    children={item.subtitle}
                                />
                            )}
                        </Box>
                        <Text
                            fontWeight='bold'
                            children={item.value}
                        />
                    </Flex>
                ))}
            </Box>
        );
    };

    return (
        <HistorySectionCard
            section={section}
            dataTestId='history-year-at-glance-section'
            headerMarginBottom={4}
            minHeight={isPlaceholder ? PLACEHOLDER_SECTION_MIN_HEIGHT : null}
        >
            {renderContent()}
        </HistorySectionCard>
    );
};

HistoryYearAtGlanceSection.propTypes = {
    section: PropTypes.shape({
        title: PropTypes.string.isRequired,
        status: PropTypes.string.isRequired,
        cardStyle: PropTypes.oneOf(Object.values(HISTORY_CARD_STYLES)),
        items: PropTypes.arrayOf(PropTypes.object),
        placeholder: PropTypes.object,
        emptyState: PropTypes.shape({
            illustration: PropTypes.string,
            body: PropTypes.string,
            primaryAction: PropTypes.shape({
                label: PropTypes.string,
                action: PropTypes.func
            })
        }),
        error: PropTypes.object,
        retry: PropTypes.func
    }).isRequired
};

const styles = {
    row: {
        borderBottom: `1px solid ${colors.lightGray}`,
        paddingBottom: space[3],
        marginBottom: space[3],
        '&:last-child': {
            borderBottom: 0,
            paddingBottom: 0,
            marginBottom: 0
        }
    }
};

export default wrapFunctionalComponent(HistoryYearAtGlanceSection, 'HistoryYearAtGlanceSection');
